import React from "react";
import {
	CalendarDay,
	type ClassNames,
	DayPicker,
	type MonthCaptionProps,
	type PropsSingle,
	type WeekProps,
} from "react-day-picker";

import { faChevronLeft, faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import {
	addDays,
	addMonths,
	getDay,
	getUnixTime,
	getWeek,
	isSameMonth,
	isSameYear,
	isThisYear,
	isToday,
	startOfWeek,
} from "date-fns";
import { noop } from "lodash";

import useFirstWeekday from "~/calendar/hooks/useFirstWeekday";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Button, toast } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import { CalendarFilterContext } from "../CalendarPage";
import { SidePanelNavContext } from "../SidePanel";
import PanelToggler from "../Toolbar/PanelToggler";

import styles from "./SmallCalendar.module.css";

const WEEKDAYS_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export type SmallCalendarContextType = {
	month: Date;
	setMonth: (date: Date) => void;
};

export const SmallCalendarContext = React.createContext<SmallCalendarContextType>({
	month: new Date(),
	setMonth: noop,
});

function NavHeader({ calendarMonth }: MonthCaptionProps) {
	const { month, setMonth } = React.useContext(SmallCalendarContext);

	const label = React.useMemo(
		() =>
			calendarMonth.date.toLocaleString(undefined, {
				month: "long",
				...(!isThisYear(calendarMonth.date) ? { year: "numeric" } : {}),
			}),
		[calendarMonth],
	);

	const navigate = React.useCallback(
		(to: "prev" | "current" | "next") => () => {
			dispatchAnalyticsTrack("Small calendar navigation", { feature: "Calendar", to });

			switch (to) {
				case "prev":
					return setMonth(addMonths(month, -1));
				case "current":
					return setMonth(new Date());
				case "next":
					return setMonth(addMonths(month, 1));
			}
		},
		[month, setMonth],
	);

	return (
		<div className={styles.navHeader}>
			<div className={styles.leftHeader}>
				<span className={styles.navButtons}>
					<IconButton icon={faChevronLeft} className={styles.navButton} onClick={navigate("prev")} />
					<IconButton icon={faChevronRight} className={styles.navButton} onClick={navigate("next")} />
				</span>
				<div className={styles.monthLabel}>{label}</div>
			</div>
			<div className={styles.panelToggler}>
				<PanelToggler />
			</div>
		</div>
	);
}

function Weekdays() {
	const firstWeekDay = useFirstWeekday();
	const weekdayLabels = React.useMemo(() => {
		const start = WEEKDAYS_SHORT.slice(0, firstWeekDay);
		const end = WEEKDAYS_SHORT.slice(firstWeekDay);
		return [...end, ...start];
	}, [firstWeekDay]);

	return (
		<thead>
			<tr className={styles.week}>
				{weekdayLabels.map(day => (
					<th scope="col" key={day} className={styles.weekday}>
						{day[0]}
					</th>
				))}
			</tr>
		</thead>
	);
}

const separateDatesByWeekend = (days: CalendarDay[]) => {
	if (getDay(days[0].date) === 0) {
		return [[days[0]], days.slice(1, 6), [days[6]]];
	} else if (getDay(days[0].date) === 1) {
		return [[], days.slice(0, 5), days.slice(5)];
	} else if (getDay(days[0].date) === 6) {
		return [days.slice(0, 2), days.slice(2), []];
	} else {
		return [[], days, []];
	}
};

function DayCell(props: { date: Date; calendarMonth: Date }) {
	const { date, calendarMonth } = props;
	const { setWeek } = React.useContext(SidePanelNavContext);

	const { showWeekends, toggleShowWeekends } = React.useContext(CalendarFilterContext);

	const onClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Small calendar date clicked", { feature: "Calendar", date });
		setWeek(date);

		const isWeekend = getDay(date) === 0 || getDay(date) === 6;

		if (!showWeekends && isWeekend) {
			const day = new Intl.DateTimeFormat(undefined, {
				month: "long",
				day: "numeric",
			}).format(date);

			const weekday = new Intl.DateTimeFormat(undefined, {
				weekday: "long",
			}).format(date);

			toast.dismiss();
			toast.warn(`${day} is a ${weekday}`, {
				title: "Weekends are hidden",
				actions: (
					<Button variant="secondary" size="normal" onClick={toggleShowWeekends}>
						Show weekends
					</Button>
				),
				closeButton: true,
			});
		}
	}, [setWeek, date, showWeekends, toggleShowWeekends]);

	return (
		<div
			className={cx(styles.dayCell, {
				[styles.dayToday]: isToday(date),
				[styles.dayOutside]: !isSameMonth(calendarMonth, date),
			})}
		>
			<button className={styles.day} type="button" onClick={onClick}>
				{date.getDate()}
			</button>
		</div>
	);
}

function Week({ week: { weekNumber, days } }: WeekProps) {
	const { week } = React.useContext(SidePanelNavContext);
	const { showWeekends } = React.useContext(CalendarFilterContext);

	const firstWeekDay = useFirstWeekday();

	const sameWeekAsBigCalendar =
		isSameYear(week, days[0].date) && getWeek(week, { weekStartsOn: firstWeekDay }) === weekNumber;

	if (showWeekends || !sameWeekAsBigCalendar) {
		return (
			<tr>
				<td colSpan={7}>
					<span className={cx(styles.week, { [styles.weekHighlight]: sameWeekAsBigCalendar })}>
						{days.map(day => (
							<DayCell key={getUnixTime(day.date)} date={day.date} calendarMonth={day.displayMonth} />
						))}
					</span>
				</td>
			</tr>
		);
	} else {
		const [beforeHighlight, inHighlight, afterHighlight] = separateDatesByWeekend(days);

		return (
			<tr>
				<td colSpan={7}>
					<span className={styles.week}>
						{beforeHighlight.map(day => (
							<DayCell key={getUnixTime(day.date)} date={day.date} calendarMonth={day.displayMonth} />
						))}
						<span className={cx({ [styles.weekHighlight]: sameWeekAsBigCalendar })}>
							{inHighlight.map(day => (
								<DayCell key={getUnixTime(day.date)} date={day.date} calendarMonth={day.displayMonth} />
							))}
						</span>
						{afterHighlight.map(day => (
							<DayCell key={getUnixTime(day.date)} date={day.date} calendarMonth={day.displayMonth} />
						))}
					</span>
				</td>
			</tr>
		);
	}
}

export default function SmallCalendar() {
	const { showWeekends } = React.useContext(CalendarFilterContext);
	const { week, setWeek } = React.useContext(SidePanelNavContext);
	const [month, setMonth] = React.useState(new Date());

	const monthRef = React.useRef(month);
	React.useEffect(() => {
		monthRef.current = month;
	}, [month]);
	React.useEffect(() => {
		if (!isSameMonth(week, monthRef.current)) {
			setMonth(week);
		}
	}, [week]);

	const onSelect = React.useCallback<NonNullable<PropsSingle["onSelect"]>>(
		(_, selectedDay) => setWeek(selectedDay),
		[setWeek],
	);

	const classNames: Partial<ClassNames> = {
		root: styles.root,
		months: styles.fullWidth,
		month: styles.fullWidth,
		// eslint-disable-next-line camelcase
		month_grid: styles.fullWidth,
		weeks: styles.weeks,
	};

	const firstWeekDay = useFirstWeekday();

	const daysInBigCalendar = React.useMemo(() => {
		const firstDayOfWeek = startOfWeek(week, { weekStartsOn: firstWeekDay === 0 && showWeekends ? 0 : 1 });
		const days = [
			firstDayOfWeek,
			addDays(firstDayOfWeek, 1),
			addDays(firstDayOfWeek, 2),
			addDays(firstDayOfWeek, 3),
			addDays(firstDayOfWeek, 4),
		];

		if (showWeekends) {
			days.push(addDays(firstDayOfWeek, 5));
			days.push(addDays(firstDayOfWeek, 6));
		}

		return days;
	}, [week, firstWeekDay, showWeekends]);

	return (
		<SmallCalendarContext.Provider value={React.useMemo(() => ({ month, setMonth }), [month])}>
			<DayPicker
				mode="single"
				showOutsideDays
				hideNavigation
				weekStartsOn={firstWeekDay}
				classNames={classNames}
				components={{ MonthCaption: NavHeader, Weekdays, Week }}
				modifiers={{
					visibleOnBigCalendar: daysInBigCalendar,
				}}
				modifiersClassNames={{
					visibleOnBigCalendar: styles.visibleOnBigCalendar,
				}}
				onSelect={onSelect}
				month={month}
				onMonthChange={setMonth}
			/>
		</SmallCalendarContext.Provider>
	);
}
